import React, { forwardRef, cloneElement } from "react";
import { Backdrop, Box, Modal as MuiModal } from "@mui/material";
import { animated, useSpring } from "@react-spring/web";
import { node, bool, func } from "prop-types";

const Fade = forwardRef<HTMLDivElement, any>(function Fade(props, ref) {
  const { children, in: open, onClick, onEnter, onExited, ...other } = props;
  const style = useSpring({
    from: {
      opacity: 0,
    },
    to: {
      opacity: open ? 1 : 0,
    },
    onStart: () => {
      if (open && onEnter) {
        onEnter(null, true);
      }
    },
    onRest: () => {
      if (!open && onExited) {
        onExited(null, true);
      }
    },
  });

  return (
    <animated.div ref={ref} style={style} {...other}>
      {cloneElement(children, {
        onClick,
      })}
    </animated.div>
  );
});

Fade.propTypes = {
  children: node.isRequired,
  in: bool.isRequired,
  onClick: func,
  onEnter: func,
  onExited: func,
};

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  minWidth: 375,
  bgcolor: "background.paper",
  border: "1px solid",
  borderColor: "secondary.200",
  borderRadius: "12px",
  boxShadow:
    "0px 4px 6px -2px rgba(0, 0, 0, 0.06), 0px 10px 15px -3px rgba(0, 0, 0, 0.1)",
};

type ModalProps = React.PropsWithChildren<{
  open: boolean;
  onClose: () => void;
}>;

/**
 *
 * @param props
 */
export function Modal(props: ModalProps) {
  const { open, onClose, children } = props;

  return (
    <MuiModal
      aria-labelledby="spring-modal-title"
      aria-describedby="spring-modal-description"
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{
        backdrop: Backdrop,
      }}
    >
      <Fade in={open}>
        <Box sx={style}>{children}</Box>
      </Fade>
    </MuiModal>
  );
}

Modal.propTypes = {
  open: bool.isRequired,
  onClose: func.isRequired,
  children: node,
};
